import { useEffect, useState } from 'react';
import { toast } from '../toast';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const PROMPT_KEY = '__wcf_pwa_prompt';

export default function InstallPanel() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [showIos, setShowIos] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Cache any previously-captured prompt event on window so it survives navigation.
    setDeferred((window as unknown as Record<string, unknown>)[PROMPT_KEY] as BeforeInstallPromptEvent | null ?? null);
    function onPrompt(e: Event) {
      e.preventDefault();
      const ev = e as BeforeInstallPromptEvent;
      (window as unknown as Record<string, unknown>)[PROMPT_KEY] = ev;
      setDeferred(ev);
    }
    function onInstalled() {
      (window as unknown as Record<string, unknown>)[PROMPT_KEY] = null;
      setDeferred(null);
      setInstalled(true);
    }
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches
      || (navigator as unknown as { standalone?: boolean }).standalone === true;
    if (standalone) setInstalled(true);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  async function install() {
    if (!deferred) return;
    try {
      await deferred.prompt();
      const r = await deferred.userChoice;
      if (r.outcome === 'accepted') {
        toast.success('Installing…');
        setInstalled(true);
      } else {
        toast.error('Install dismissed.');
      }
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      (window as unknown as Record<string, unknown>)[PROMPT_KEY] = null;
      setDeferred(null);
    }
  }

  async function forceRefresh() {
    setRefreshing(true);
    try {
      // Unregister all service workers so the next load fetches a fresh shell.
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      // Clear the Cache API entirely.
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      toast.success('Caches cleared. Reloading…');
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error((e as Error).message);
      setRefreshing(false);
    }
  }

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iphone|ipad|ipod/i.test(ua);

  return (
    <section className="card p-3 space-y-2">
      <h2 className="text-xs uppercase tracking-widest text-dim mb-1">Install / Update</h2>
      <p className="text-[11px] text-dim">
        {installed
          ? 'Running from the home-screen app. Use Force refresh if something looks stuck.'
          : 'Add the app to your home screen for lock-screen push notifications and fullscreen mode.'}
      </p>
      <div className="flex flex-wrap gap-2 pt-1">
        {!installed && deferred && (
          <button
            onClick={install}
            className="h-9 px-3 rounded-lg bg-pitch-500 text-ink-950 text-xs font-semibold"
          >Install app</button>
        )}
        {!installed && !deferred && isIOS && (
          <button
            onClick={() => setShowIos(true)}
            className="h-9 px-3 rounded-lg bg-ink-800 text-chalk text-xs font-semibold"
          >How to install on iOS</button>
        )}
        {!installed && !deferred && !isIOS && (
          <span className="text-[10px] text-dim self-center">Your browser hasn't offered install yet — try a different browser, or use the menu's "Add to home screen".</span>
        )}
        <button
          onClick={forceRefresh}
          disabled={refreshing}
          className="h-9 px-3 rounded-lg bg-ink-800 text-chalk text-xs font-semibold disabled:opacity-50"
        >{refreshing ? 'Refreshing…' : 'Force refresh'}</button>
      </div>
      <p className="text-[10px] text-dim">
        Browsers don't allow scripts to remove a previously-installed home-screen icon. Use Force refresh to wipe the service worker + cache; the existing icon then loads the newest build on next launch.
      </p>

      {showIos && <IosInstructions onClose={() => setShowIos(false)} />}
    </section>
  );
}

function IosInstructions({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-end sm:place-items-center p-0 sm:p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="card w-full sm:w-96 p-5 rounded-t-3xl sm:rounded-2xl space-y-3 ring-1 ring-pitch-500/20"
        style={{ paddingBottom: 'max(1.25rem, calc(env(safe-area-inset-bottom) + 1rem))' }}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Install on iOS</h3>
          <button onClick={onClose} className="text-dim text-xl leading-none px-2">×</button>
        </div>
        <ol className="space-y-2 text-sm">
          <li className="flex gap-2.5">
            <span className="shrink-0 w-6 h-6 rounded-full bg-pitch-500/20 text-pitch-400 grid place-items-center text-xs font-bold">1</span>
            <span>Open this page in <span className="font-semibold">Safari</span> (not Chrome / in-app browser).</span>
          </li>
          <li className="flex gap-2.5">
            <span className="shrink-0 w-6 h-6 rounded-full bg-pitch-500/20 text-pitch-400 grid place-items-center text-xs font-bold">2</span>
            <span>Tap the <span className="font-semibold">Share</span> button (square + arrow) at the bottom of Safari.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="shrink-0 w-6 h-6 rounded-full bg-pitch-500/20 text-pitch-400 grid place-items-center text-xs font-bold">3</span>
            <span>Scroll down and pick <span className="font-semibold">Add to Home Screen</span>.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="shrink-0 w-6 h-6 rounded-full bg-pitch-500/20 text-pitch-400 grid place-items-center text-xs font-bold">4</span>
            <span>Tap <span className="font-semibold">Add</span> — the icon lands on your home screen.</span>
          </li>
        </ol>
        <p className="text-[11px] text-dim pt-1">
          If you already have an older icon, delete it first (long-press → Remove App). Apple doesn't let websites remove icons for you.
        </p>
        <button
          onClick={onClose}
          className="w-full h-10 rounded-lg bg-pitch-500 text-ink-950 font-semibold text-sm"
        >Got it</button>
      </div>
    </div>
  );
}
